/**
 * LocalStorage Manager
 *  - Manage localStorage keys, avoid naming errors and duplicates
 */

import logger from './logger'

// Define all localStorage keys
export const STORAGE_KEYS = {
    // User related
    USER_PROFILE: 'profile',

    // Payment related
    PAYMENT_ORIGIN: 'paymentOrigin',
    CHECKOUT_ASSISTANT_PAYMENT_SUCCESS: 'checkoutAssistantPaymentSuccess',
    CHECKOUT_ASSISTANT_PAYMENT_CANCEL: 'checkoutAssistantPaymentCancel',

    // Cash register related
    CHECKOUT_ASSISTANT_CASH_REGISTER_ID: 'checkout_assistant_cash_register_id',
    SELECTED_CASH_REGISTER_ID: 'selected_cash_register_id',

    // Membership related
    ACCESS_TOKEN: 'accessToken',
    TOKEN_EXPIRES_AT: 'tokenExpiresAt',
    REFRESH_TOKEN: 'refreshToken',

    // Employee related
    EMPLOYEE_ACCESS_TOKEN: 'employeeAccessToken',
    EMPLOYEE_TOKEN_EXPIRES_AT: 'employeeTokenExpiresAt',
}

/**
 * Safe localStorage operations
 */
class LocalStorageManager {
    /**
     * Get localStorage item
     * @param {string} key - storage key
     * @param {any} defaultValue - default value
     * @returns {any} stored value or default value
     */
    getItem(key, defaultValue = null) {
        try {
            if (typeof window === 'undefined') return defaultValue
            const item = window.localStorage.getItem(key)
            return item !== null ? item : defaultValue
        } catch (error) {
            logger.warn(`Failed to get localStorage item "${key}":`, error)
            return defaultValue
        }
    }

    /**
     * Get and parse JSON formatted localStorage item
     * @param {string} key - storage key
     * @param {any} defaultValue - default value
     * @returns {any} parsed object or default value
     */
    getJSON(key, defaultValue = null) {
        try {
            const item = this.getItem(key)
            if (item === null || item === 'undefined') return defaultValue
            return JSON.parse(item)
        } catch (error) {
            logger.warn(`Failed to parse JSON from localStorage item "${key}":`, error)
            return defaultValue
        }
    }

    /**
     * Set localStorage item
     * @param {string} key - storage key
     * @param {any} value - value to store
     * @returns {boolean} whether setting was successful
     */
    setItem(key, value) {
        try {
            if (typeof window === 'undefined') return false
            window.localStorage.setItem(key, value)
            return true
        } catch (error) {
            logger.warn(`Failed to set localStorage item "${key}":`, error)
            return false
        }
    }

    /**
     * Set JSON formatted localStorage item
     * @param {string} key - storage key
     * @param {any} value - value to store
     * @returns {boolean} whether setting was successful
     */
    setJSON(key, value) {
        try {
            const jsonString = JSON.stringify(value)
            return this.setItem(key, jsonString)
        } catch (error) {
            logger.warn(`Failed to stringify and set localStorage item "${key}":`, error)
            return false
        }
    }

    /**
     * Remove localStorage item
     * @param {string} key - storage key
     * @returns {boolean} whether removal was successful
     */
    removeItem(key) {
        try {
            if (typeof window === 'undefined') return false
            window.localStorage.removeItem(key)
            return true
        } catch (error) {
            logger.warn(`Failed to remove localStorage item "${key}":`, error)
            return false
        }
    }

    /**
     * Clear all localStorage
     * @returns {boolean} whether clearing was successful
     */
    clear() {
        try {
            if (typeof window === 'undefined') return false
            window.localStorage.clear()
            return true
        } catch (error) {
            logger.warn('Failed to clear localStorage:', error)
            return false
        }
    }

    /**
     * Check if localStorage is available
     * @returns {boolean} whether localStorage is available
     */
    isAvailable() {
        try {
            if (typeof window === 'undefined') return false
            const testKey = '__localStorage_test__'
            window.localStorage.setItem(testKey, 'test')
            window.localStorage.removeItem(testKey)
            return true
        } catch {
            return false
        }
    }
}

// Create singleton instance
export const localStorage = new LocalStorageManager()

// Helper factory to generate storage functions
const createStorageHelpers = keys => {
    const helpers = {}

    // Keywords that indicate JSON storage
    const JSON_KEYWORDS = ['USER_PROFILE', 'CHECKOUT_ASSISTANT_PAYMENT_SUCCESS', 'CHECKOUT_ASSISTANT_PAYMENT_CANCEL']

    Object.entries(keys).forEach(([name, key]) => {
        // Check if this key should be stored as JSON
        const isJSON = JSON_KEYWORDS.includes(name)
        const camelCaseName = toCamelCase(name)

        if (isJSON) {
            helpers[`get${camelCaseName}`] = () => localStorage.getJSON(key)
            helpers[`set${camelCaseName}`] = data => localStorage.setJSON(key, data)
        } else {
            helpers[`get${camelCaseName}`] = () => localStorage.getItem(key)
            helpers[`set${camelCaseName}`] = value => localStorage.setItem(key, value)
        }
        helpers[`clear${camelCaseName}`] = () => localStorage.removeItem(key)
    })

    return helpers
}

// Utility function to convert SNAKE_CASE to CamelCase
const toCamelCase = str => {
    return str
        .split('_')
        .map((word, index) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join('')
}

// Auto-generated helpers
const autoHelpers = createStorageHelpers(STORAGE_KEYS)

// Helper functions for business logic
export const storageHelpers = {
    ...autoHelpers,
}

export default localStorage
